import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useState } from "react";
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  Chip,
  Avatar,
  Box,
} from "@mui/material";
import { AlternateEmail, Edit, Delete } from "@mui/icons-material";
import { emailNotificationsType } from "../../../../utils/emailNotificationTypes";
import ModalWidget from "../../../../components/ModalWidget/ModalWidget";
import PreviewNotification from "./PreviewNotification";

type RecipientDetail = {
  id: string;
  displayName: string;
};

type EmailNotification = {
  notificationType: string;
  id: string;
  subject: string;
  body: string;
  toRecipientDetails?: RecipientDetail[];
  ccRecipientDetails?: RecipientDetail[];
};

type NotificationCardProps = {
  notification: EmailNotification;
  onEdit: (notification: EmailNotification) => void;
  onDelete: (id: string) => void;
  theme: any;
};

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onEdit,
  onDelete,
  theme,
}) => {
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [openPreviewModel, setOpenPreviewModel] = useState(false);
  const handleClose = () => setOpenPreviewModel(false);

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        background: theme.palette.mode === 'dark' ? theme.palette.gray.darkGray1 : theme.palette.background.default,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        px: 2,
      }}
    >
      <CardContent sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
          {/* <AlternateEmail fontSize="small" /> */}
          <Typography variant="body2" color={theme.palette.black.main} fontWeight={typographyTokens.fontWeights.semibold}>
            {"Notification Type:"} ({'ID: ' + notification.id})
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Box
            sx={{
              cursor: "pointer",
              px: 1,
              borderRadius: 1,
              color: theme.palette.primary.main,
              background: theme.palette.action.hover,
            }}
            onClick={() => setOpenPreviewModel(true)}
          >
            Preview
          </Box>
          <ModalWidget
            isOpen={openPreviewModel}
            onClose={handleClose}
            title={"Preview Notification"}
          >
            <PreviewNotification body={notification.body} />
          </ModalWidget>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              onClick={() => onEdit(notification)}
              sx={{
                borderRadius: 2,
                background: theme.palette.action.hover,
                ml: 1,
              }}
              size="small"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color={theme.palette.mode === 'dark' ? "primary" :"error"}
              onClick={() => setOpenDeleteModel(true)}
              sx={{
                borderRadius: 2,
                background: theme.palette.action.hover,
                ml: 1,
              }}
              size="small"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
          <ModalWidget
            isOpen={openDeleteModel}
            onClose={() => setOpenDeleteModel(false)}
            title={
              emailNotificationsType.find(
                (type) => type.type === notification.notificationType
              )?.label || notification.notificationType
            }
          >
            <Box p={2}>
              <Typography variant="body1" mb={3}>
                Are you sure you want to delete this notification?
                <b>
                  {" "}
                  {emailNotificationsType.find(
                    (type) => type.type === notification.notificationType
                  )?.label || notification.notificationType}
                </b>
              </Typography>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Chip
                  label="No"
                  color="default"
                  onClick={() => setOpenDeleteModel(false)}
                  sx={{ px: 2 }}
                />
                <Chip
                  label="Yes"
                  color="error"
                  onClick={() => {
                    onDelete(notification.id);
                    setOpenDeleteModel(false);
                  }}
                  sx={{ px: 2 }}
                />
              </Box>
            </Box>
          </ModalWidget>
        </Stack>
        <Typography fontWeight={typographyTokens.fontWeights.semibold} sx={{ mb: 2 }} color={theme.palette.black.main}>
          {emailNotificationsType.find(
            (type) => type.type === notification.notificationType
          )?.label || notification.notificationType}
        </Typography>
        <Typography
          variant="body2"
          color={theme.palette.black.main}
          fontWeight={typographyTokens.fontWeights.semibold}
        >
          {"Subject:"}
        </Typography>
        <Typography
          color={theme.palette.black.main}
          fontWeight={typographyTokens.fontWeights.medium}
          variant="body1"
          mb={1}
        >
          {notification.subject || ""}
        </Typography>
        <Typography
          variant="body2"
          color={theme.palette.black.main}
          gutterBottom
          fontWeight={typographyTokens.fontWeights.semibold}
        >
          {"Body:"}
        </Typography>
        <Typography
          variant="body1"
          color={theme.palette.black.main}
          mb={2}
          fontWeight={typographyTokens.fontWeights.medium}
        >
          {notification.body.length > 100
            ? `${notification.body.slice(0, 100)}...`
            : notification.body}
          {/* {notification.body} */}
        </Typography>
        <Typography variant="subtitle2" fontWeight={typographyTokens.fontWeights.semibold} color={theme.palette.black.main} gutterBottom>
          To Recipients:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
          {Array.isArray(notification.toRecipientDetails) &&
            notification.toRecipientDetails.map((r) => (
              <Chip
                key={r.id}
                label={r.displayName}
                size="small"
                color="primary"
                variant="outlined"
                style={{ marginLeft: 0 }}
                avatar={
                  <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                    {r.displayName[0]?.toUpperCase()}
                  </Avatar>
                }
              />
            ))}
        </Stack>
        <Typography variant="subtitle2" fontWeight={typographyTokens.fontWeights.semibold} color={theme.palette.black.main} gutterBottom>
          CC Recipients:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {Array.isArray(notification.ccRecipientDetails) &&
            notification.ccRecipientDetails.map((r) => (
              <Chip
                key={r.id}
                label={r.displayName}
                size="small"
                color={theme.palette.mode === 'dark' ?"primary" : "default"}
                variant="outlined"
                style={{ marginLeft: 0 }}
                avatar={
                  <Avatar sx={{ bgcolor: theme.palette.primary.light }}>
                    {r.displayName[0]?.toUpperCase()}
                  </Avatar>
                }
              />
            ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default NotificationCard;
